"use client"

import { AccountLockupFragment } from "@opensea/app-kit"
import { graphql, type OperationContext, useQuery } from "@opensea/graphql"
import { normalizeAddress } from "@opensea/utils"
import { useAddress } from "@/hooks/wallet"

const query = graphql(
  `
    query BasicProfileQuery($address: Address!) {
      profileByAddress(address: $address) {
        __typename
        ... on Profile {
          address
          isStaff
          isBetaTester
          allowSolanaAccess
          imageUrl
          portfolioSummary {
            estimatedTokenValue {
              native {
                unit
                symbol
                contractAddress
                chain {
                  identifier
                }
              }
            }
          }
        }
        ...AccountLockup
      }
    }
  `,
  [AccountLockupFragment],
)

const OPERATION_CONTEXT: Partial<OperationContext> = { suspense: false }

/**
 * Returns the basic profile information for the given address.
 * This should be used for all top-level things that are fetched outright e.g. top nav, side nav.
 */
export function useBasicProfile() {
  const address = useAddress()
  const [{ data, fetching }, refetch] = useQuery(
    {
      query,
      variables: { address: address ? normalizeAddress(address) : "" },
      pause: !address,
      context: OPERATION_CONTEXT,
    },
    { throwOnError: false },
  )
  const actions = { fetching, refetch }

  if (address && data?.profileByAddress.__typename === "Profile") {
    return { profile: data.profileByAddress, ...actions }
  }

  return { profile: null, ...actions }
}
